import React, { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Download, FileText, Trash2, Upload } from 'lucide-react';

// ── Public types ───────────────────────────────────────────────────────────

/** A document that has already been persisted on the server. */
export interface DocumentUploadDoc {
  id: string;
  name: string;
  size?: number;       // bytes
  uploadedAt?: string; // ISO date string
  /** false hides the delete action for this document even when onDelete is set */
  deletable?: boolean;
}

interface DocumentUploadProps {
  /** Persisted documents already stored on the server. */
  documents?: DocumentUploadDoc[];
  /** Files staged locally — chosen but not yet sent to the server. */
  stagedFiles?: File[];
  /** Show the upload drop-zone. */
  canUpload?: boolean;
  /** HTML accept string for the file picker. Default: ".pdf,.jpg,.jpeg,.png" */
  accept?: string;
  /** Maximum file size in MB. Default: 10 */
  maxSizeMb?: number;
  /** Called with validated files the user chose or dropped. */
  onFilesSelected?: (files: File[]) => void;
  /** Called when the user removes a staged file by index. */
  onStagedRemove?: (index: number) => void;
  /** Called when the user clicks download on a persisted document. */
  onDownload?: (doc: DocumentUploadDoc) => void;
  /** Called when the user clicks delete on a persisted document. */
  onDelete?: (doc: DocumentUploadDoc) => void;
  /** Show a spinner / disable the upload zone while an upload is in flight. */
  uploading?: boolean;
  /** ID of the document currently being deleted — disables its delete button. */
  deletingId?: string | null;
  /** Accessible label for the delete button. Default: "Delete document" */
  deleteLabel?: string;
  /** Called when chosen files fail size or type validation. */
  onValidationError?: (message: string) => void;
  /** Wrap in a collapsible accordion (chevron header). */
  collapsible?: boolean;
  /** Expand by default when collapsible is true. Default: true */
  defaultOpen?: boolean;
  className?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Component ──────────────────────────────────────────────────────────────

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documents = [],
  stagedFiles = [],
  canUpload = false,
  accept = '.pdf,.jpg,.jpeg,.png',
  maxSizeMb = 10,
  onFilesSelected,
  onStagedRemove,
  onDownload,
  onDelete,
  deleteLabel = 'Delete document',
  uploading = false,
  deletingId = null,
  onValidationError,
  collapsible = false,
  defaultOpen = true,
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);

  const maxBytes = maxSizeMb * 1024 * 1024;

  const validateAndEmit = (raw: FileList | File[]) => {
    const valid: File[] = [];
    const skipped: string[] = [];
    Array.from(raw).forEach(f => {
      if (!ACCEPTED_MIME.includes(f.type) || f.size > maxBytes) skipped.push(f.name);
      else valid.push(f);
    });
    if (skipped.length > 0) {
      onValidationError?.(`${skipped.length} file(s) skipped — only PDF, JPG, PNG under ${maxSizeMb} MB allowed`);
    }
    if (valid.length > 0) onFilesSelected?.(valid);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    validateAndEmit(e.dataTransfer.files);
  };

  const totalCount = documents.length + stagedFiles.length;

  // ── Inner content (shared by both collapsible and flat layouts) ──────────

  const body = (
    <div className={collapsible ? 'px-4 pb-4 border-t border-slate-100 pt-3 space-y-2' : 'p-4 space-y-2'}>

      {/* Upload drop-zone */}
      {canUpload && (
        <div
          className={`border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-all duration-200 ${
            dragActive
              ? 'border-blue-400 bg-blue-50 shadow-sm'
              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
          }`}
          onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
          onDragLeave={e => {
            e.preventDefault();
            e.stopPropagation();
            // Only clear when the cursor leaves the drop zone entirely, not when
            // it moves to a child element (icon, label, text) inside it.
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDragActive(false);
            }
          }}
          onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center">
            <Upload size={32} className={`mx-auto mb-2 ${dragActive ? 'text-blue-500' : 'text-slate-400'}`} />
            <p className="text-sm font-medium text-slate-700 mb-1">
              {dragActive ? 'Drop files here' : 'Drag files here or click to upload'}
            </p>
            <p className="text-xs text-slate-500 mb-3">PDF, JPG, PNG — up to {maxSizeMb} MB each</p>
            <label className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors cursor-pointer select-none">
              <Upload size={16} className="mr-2 shrink-0" />
              {uploading ? 'Uploading…' : 'Choose Files'}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={accept}
                className="hidden"
                disabled={uploading}
                onChange={e => {
                  if (e.target.files) validateAndEmit(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>
      )}

      {/* Staged files (selected locally, not yet uploaded) */}
      {stagedFiles.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-slate-500 pt-1">Staged for upload</p>
          {stagedFiles.map((file, i) => (
            <div key={`${file.name}-${i}`} className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={15} className="text-blue-600 shrink-0" />
                <span className="text-sm font-medium text-slate-800 truncate min-w-0">{file.name}</span>
                <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap">{formatFileSize(file.size)}</span>
              </div>
              {onStagedRemove && (
                <button
                  type="button"
                  onClick={() => onStagedRemove(i)}
                  className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-white shrink-0 ml-2"
                  aria-label="Remove staged file"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Persisted documents */}
      {documents.map(doc => (
        <div key={doc.id} className="flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-md">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={15} className="text-slate-400 shrink-0" />
            <span className="text-sm font-medium text-slate-800 truncate min-w-0">{doc.name}</span>
            {(doc.size || doc.uploadedAt) && (
              <span className="text-xs text-slate-400 shrink-0 whitespace-nowrap">
                {[
                  doc.size ? formatFileSize(doc.size) : '',
                  doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : '',
                ].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {onDownload && (
              <button
                type="button"
                onClick={() => onDownload(doc)}
                className="p-1.5 text-slate-400 hover:text-blue-600 rounded-md hover:bg-slate-50"
                aria-label="Download document"
              >
                <Download size={14} />
              </button>
            )}
            {onDelete && doc.deletable !== false && (
              <button
                type="button"
                onClick={() => onDelete(doc)}
                disabled={deletingId === doc.id}
                className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-slate-50 disabled:opacity-40"
                aria-label={deleteLabel}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Empty state */}
      {totalCount === 0 && !canUpload && (
        <p className="text-sm text-slate-400 text-center py-2">No documents attached</p>
      )}
    </div>
  );

  // ── Title bar (shared) ───────────────────────────────────────────────────

  const badge = totalCount > 0
    ? <span className="text-xs text-slate-400">{totalCount} file{totalCount !== 1 ? 's' : ''}</span>
    : null;

  const spinner = uploading
    ? <span className="text-xs text-blue-500 animate-pulse ml-1">Uploading…</span>
    : null;

  // ── Flat layout ──────────────────────────────────────────────────────────

  if (!collapsible) {
    return (
      <div className={`border border-slate-200 rounded-lg overflow-hidden ${className}`}>
        <div className="px-4 py-3 border-b border-slate-100 bg-white flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">Documents</span>
          {badge}
          {spinner}
        </div>
        {body}
      </div>
    );
  }

  // ── Collapsible layout ───────────────────────────────────────────────────

  return (
    <div className={`border border-slate-200 rounded-lg overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-semibold text-slate-800">Documents</span>
          {badge}
          {spinner}
        </div>
      </button>
      {open && body}
    </div>
  );
};

export default DocumentUpload;
